import { useCallback } from 'react';

import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';
import type { CampaignTrackingErrorData, CampaignTrackingErrorName } from '#src/app/shared/hooks/campaignTracking';

import type { PetitionUpdateType } from '../types';

type PetitionUpdateLikeTrackingEvent =
	| 'petition_update_like_click'
	| 'petition_update_like_success'
	| 'petition_update_like_failure';

type PetitionUpdateLikeTrackingOptions = {
	likeState: boolean;
	errors?: string;
};

type TrackLikePetitionUpdate = (
	trackingEvent: PetitionUpdateLikeTrackingEvent,
	options?: PetitionUpdateLikeTrackingOptions,
) => void;

type Result = ModelHookResult<
	undefined,
	{
		trackLikePetitionUpdate: TrackLikePetitionUpdate;
		trackError: (eventName: CampaignTrackingErrorName, e?: unknown, errorData?: CampaignTrackingErrorData) => void;
	}
>;

type UseLikePetitionUpdateTrackingParams = {
	petitionUpdateLikeTrackingData: {
		petition_id: string;
		petition_update_id: string;
		update_type: PetitionUpdateType;
		liker_id: string | undefined;
	};
};

export function useLikePetitionUpdateTracking({
	petitionUpdateLikeTrackingData,
}: UseLikePetitionUpdateTrackingParams): Result {
	const { track, trackError } = useCampaignTracking();

	const trackLikePetitionUpdate: TrackLikePetitionUpdate = useCallback(
		(trackingEvent, options) => {
			track(trackingEvent, {
				...petitionUpdateLikeTrackingData,
				liked_state: options?.likeState,
				...(trackingEvent === 'petition_update_like_failure' ? { errors: options?.errors } : {}),
			});
		},
		[track, petitionUpdateLikeTrackingData],
	);

	return {
		actions: {
			trackLikePetitionUpdate,
			trackError,
		},
	};
}
